import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Container, Table, Button, Form, InputGroup, OverlayTrigger, Tooltip, Modal, Spinner } from 'react-bootstrap';
import './InvoiceList.css';
import api from '../services/api';
import { showToast } from '../components/ToastNotification';
import { downloadBlob, exportMoneyXml, exportInvoicesXlsx, sklonFaktur } from '../components/invoiceActions';
import SearchableSelect from '../components/SearchableSelect';
import useColumnWidths from '../components/useColumnWidths';
import ColumnWidthsReset from '../components/ColumnWidthsReset';
import InvoiceDeleteModal from '../components/InvoiceDeleteModal';
import InvoiceEmailModal from '../components/InvoiceEmailModal';

const ITEMS_PER_PAGE = 50;

/** Sloupce výpisu v pořadí hlavičky — klíče, pod kterými se ukládají šířky. */
const SLOUPCE = [
  'vyber', 'cislo', 'typ', 'zakaznik', 'obchodnik', 'projekt', 'vystaveni', 'duzp',
  'splatnost', 'typPlatby', 'zasilani', 'posledniUhrada', 'bezDph', 'celkem',
  'platby', 'status', 'pohledavka', 'akce',
];

// ── Helpers ─────────────────────────────────────────────────────────────
const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  // Handle datetime strings (trim time part if present)
  const datePart = dateStr.substring(0, 10);
  const d = new Date(datePart + 'T00:00:00');
  if (isNaN(d.getTime())) return dateStr;
  const day = String(d.getDate()).padStart(2, '0');
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const year = d.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatPrice = (value) => {
  if (value === null || value === undefined || value === '') return '—';
  const num = parseFloat(value);
  if (isNaN(num)) return '—';
  return num.toLocaleString('cs-CZ', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
};

/**
 * Calculate days overdue: positive = days after due_date, negative/0 = not overdue
 */
const getOverdueDays = (dueDate, paid, status) => {
  if (paid || status === 'uhrazena' || status === 'storno') return -1;
  if (!dueDate) return -1;
  const duePart = dueDate.substring(0, 10);
  const due = new Date(duePart + 'T00:00:00');
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const diff = Math.floor((today - due) / 864e5);
  return diff;
};

// ── Badge components ───────────────────────────────────────────────────
function StatusBadge({ status }) {
  const map = {
    uhrazena:   { cls: 'badge-paid',      label: 'Uhrazená' },
    castecna:   { cls: 'badge-partial',    label: 'Částečně' },
    neuhrazena: { cls: 'badge-unpaid',     label: 'Neuhrazená' },
    storno:     { cls: 'badge-cancelled',  label: 'Storno' },
  };
  const m = map[status];
  if (!m) return <span>—</span>;
  return <span className={`badge-status ${m.cls}`}>{m.label}</span>;
}

function OverdueBadge({ days }) {
  if (days <= 0) return <span className="badge-overdue badge-overdue-ok">OK</span>;
  if (days <= 7) return <span className="badge-overdue badge-overdue-7">{days} dní</span>;
  if (days <= 14) return <span className="badge-overdue badge-overdue-14">{days} dní</span>;
  if (days <= 60) return <span className="badge-overdue badge-overdue-30">{days} dní</span>;
  return <span className="badge-overdue badge-overdue-60">{days} dní</span>;
}

function TypeBadge({ type, label }) {
  const map = {
    Invoice: { cls: 'badge-type-invoice' },
    Credit: { cls: 'badge-type-credit' },
    Proforma: { cls: 'badge-type-proforma' },
    Cancel: { cls: 'badge-type-cancel' },
    Confirm: { cls: 'badge-type-confirm' },
    Debit: { cls: 'badge-type-debit' },
    Bonus: { cls: 'badge-type-bonus' },
    MultiCorrectionInvoice: { cls: 'badge-type-multi' },
  };
  const m = map[type] || { cls: 'badge-type-default' };
  return <span className={`badge-invoice-type ${m.cls}`}>{label || type || '—'}</span>;
}

/**
 * Způsob zasílání dokladu (`enum_communication_method`) — bere se z karty
 * zákazníka. Ve výpisu zkráceně a s ikonou, aby na první pohled šlo oddělit,
 * co odejde e-mailem a co se musí vytisknout a dát na poštu.
 */
function ZpusobZasilani({ method, label }) {
  const map = {
    EMAIL: { icon: 'bi-envelope', text: 'E-mail' },
    POST: { icon: 'bi-mailbox', text: 'Pošta' },
    EXTERNAL_SYSTEM: { icon: 'bi-box-arrow-up-right', text: 'Externě' },
  };
  const m = map[method];
  if (!m) {
    return <span className="small-muted" title="Na kartě zákazníka není vyplněný způsob zasílání">—</span>;
  }
  return (
    <span className="small-muted text-nowrap" title={label || m.text}>
      <i className={`bi ${m.icon} me-1`}></i>{m.text}
    </span>
  );
}

// ════════════════════════════════════════════════════════════════════════
// Main component
// ════════════════════════════════════════════════════════════════════════
function InvoiceList({ openTab, can }) {
  // ── Ref for scroll-to-top on page change ────────────────────────────
  const containerRef = useRef(null);

  const scrollToTop = useCallback(() => {
    // Scroll the parent .content-area container to top
    const el = containerRef.current;
    if (el) {
      const scrollParent = el.closest('.content-area');
      if (scrollParent) {
        scrollParent.scrollTo({ top: 0, behavior: 'smooth' });
      }
    }
  }, []);

  // ── Filter state ───────────────────────────────────────────────────
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState('');
  const [paymentTypeFilter, setPaymentTypeFilter] = useState('');
  // Způsob zasílání dokladu — podle něj se vybírá, co poslat e-mailem a co vytisknout
  const [communicationFilter, setCommunicationFilter] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');

  // ── Sorting ────────────────────────────────────────────────────────
  const [sortColumn, setSortColumn] = useState('creation_date');
  const [sortOrder, setSortOrder] = useState('desc');

  // ── Pagination ─────────────────────────────────────────────────────
  const [currentPage, setCurrentPage] = useState(1);

  // ── Koš (smazané doklady, migrace 037) ─────────────────────────────
  const [showDeleted, setShowDeleted] = useState(false);
  const [deleteInvoiceId, setDeleteInvoiceId] = useState(null);
  const canDelete = typeof can === 'function' ? can('invoice.delete') : true;

  // ── Data ───────────────────────────────────────────────────────────
  const [invoices, setInvoices] = useState([]);
  const [types, setTypes] = useState([]);
  const [paymentTypes, setPaymentTypes] = useState([]);

  // Faktura bez objednávky — dialog založí hlavičku, položky se dopisují v detailu.
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createForm, setCreateForm] = useState({
    customerId: '',
    customerLabel: '',
    type: 'Invoice',
    paymentType: 'Wire',
    taxSupportDate: '',
    dueDate: '',
  });
  const [communicationMethods, setCommunicationMethods] = useState([]);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(true);

  // ── Selection state ────────────────────────────────────────────────
  // Map id → řádek faktury. Držíme celý řádek, protože součet i modaly
  // potřebují částky i u faktur, které už nejsou na aktuální stránce.
  const [selectedRows, setSelectedRows] = useState(new Map());

  // ── Fetch invoices from API ────────────────────────────────────────
  const fetchInvoices = useCallback(async () => {
    setLoading(true);
    try {
      const result = await api.getInvoices({
        search: debouncedSearch,
        type: typeFilter,
        paymentType: paymentTypeFilter,
        communicationMethod: communicationFilter,
        dateFrom: dateFrom,
        dateTo: dateTo,
        page: currentPage,
        limit: ITEMS_PER_PAGE,
        sort: sortColumn,
        order: sortOrder,
        deleted: showDeleted ? 1 : '',
      });

      setInvoices(result.data || []);
      if (result.types) {
        setTypes(result.types);
      }
      if (result.communicationMethods) {
        setCommunicationMethods(result.communicationMethods);
      }
      if (result.paymentTypes) {
        setPaymentTypes(result.paymentTypes);
      }
      const pagination = result.pagination || {};
      setTotalPages(pagination.totalPages || 1);
      setTotalItems(pagination.total || 0);
    } catch (err) {
      console.error('Failed to fetch invoices:', err);
      setInvoices([]);
    } finally {
      setLoading(false);
      scrollToTop();
    }
  }, [debouncedSearch, typeFilter, paymentTypeFilter, communicationFilter, dateFrom, dateTo, currentPage, sortColumn, sortOrder, showDeleted, scrollToTop]);

  useEffect(() => {
    fetchInvoices();
  }, [fetchInvoices]);

  // Otevřená záložka Fakturace se sama zaktualizuje, když někde vznikne
  // nebo se změní faktura (vytvoření z objednávky, z projektu, editace).
  useEffect(() => {
    const handler = () => fetchInvoices();
    window.addEventListener('invoices:refresh', handler);
    return () => window.removeEventListener('invoices:refresh', handler);
  }, [fetchInvoices]);

  // Debounce search (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchTerm);
      setCurrentPage(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const safePage = Math.min(currentPage, totalPages || 1);

  // ── Sort handler ───────────────────────────────────────────────────
  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortColumn(column);
      setSortOrder('desc');
    }
    setCurrentPage(1);
  };

  const SortIcon = ({ column }) => {
    if (sortColumn !== column) return <i className="bi bi-chevron-expand text-muted ms-1" style={{ fontSize: '0.7rem' }}></i>;
    return sortOrder === 'asc'
      ? <i className="bi bi-chevron-up ms-1" style={{ fontSize: '0.7rem' }}></i>
      : <i className="bi bi-chevron-down ms-1" style={{ fontSize: '0.7rem' }}></i>;
  };

  // ── Filter handlers ────────────────────────────────────────────────
  const handleReset = () => {
    setSearchTerm('');
    setDebouncedSearch('');
    setTypeFilter('');
    setPaymentTypeFilter('');
    setDateFrom('');
    setDateTo('');
    setSortColumn('creation_date');
    setSortOrder('desc');
    setCurrentPage(1);
    setSelectedRows(new Map());
  };

  // ── Checkbox handlers ──────────────────────────────────────────────
  const allChecked = invoices.length > 0 && invoices.every((f) => selectedRows.has(f.id));

  const toggleAll = () => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      invoices.forEach((f) => (allChecked ? next.delete(f.id) : next.set(f.id, f)));
      return next;
    });
  };

  const toggleRow = (invoice) => {
    setSelectedRows((prev) => {
      const next = new Map(prev);
      if (next.has(invoice.id)) next.delete(invoice.id);
      else next.set(invoice.id, invoice);
      return next;
    });
  };

  const hasSelection = selectedRows.size > 0;
  const selectedIds = [...selectedRows.keys()];

  // Šířky sloupců tažením — ukládají se uživateli do DB (migrace 048).
  const { wrapRef, colgroup, tableStyle, resizerProps, maUlozene, vychozi } =
    useColumnWidths('invoices.list', SLOUPCE);

  // ── Faktura bez objednávky ─────────────────────────────────────────
  // Jednorázová věc, kvůli které nemá smysl zakládat médium, projekt a objednávku.
  // Dialog vyplní jen hlavičku; položky se dopisují v detailu faktury, kde na to
  // formulář (název, množství, cena, DPH) už je.
  const searchCompanies = useCallback(async (query) => {
    const res = await api.searchCompanies(query);
    return (res.data || []).map(c => ({ id: c.id, label: c.company_name || `#${c.id}` }));
  }, []);

  const otevriNovouFakturu = () => {
    setCreateForm({
      customerId: '',
      customerLabel: '',
      type: 'Invoice',
      paymentType: 'Wire',
      taxSupportDate: new Date().toISOString().substring(0, 10),
      dueDate: '',
    });
    setShowCreateModal(true);
  };

  const vytvorFakturu = async () => {
    if (creating || !createForm.customerId) return;
    setCreating(true);
    try {
      const res = await api.createManualInvoice({
        customerId: createForm.customerId,
        type: createForm.type,
        paymentType: createForm.paymentType,
        taxSupportDate: createForm.taxSupportDate || null,
        dueDate: createForm.dueDate || null,
      });
      const faktura = res.data || {};
      setShowCreateModal(false);
      showToast(`Faktura ${faktura.number} vytvořena — doplňte položky`, 'success');
      if (faktura.aresWarning) {
        showToast('Fakturační adresa se nenačetla z ARESu, použila se uložená.', 'warning');
      }
      fetchInvoices();
      openTab(
        `invoice-${faktura.id}`,
        `Faktura ${faktura.number}`,
        `/invoices/${faktura.id}`,
        'InvoiceDetail',
        { invoiceId: faktura.id },
      );
    } catch (e) {
      showToast(e.message || 'Fakturu se nepodařilo vytvořit', 'danger');
    } finally {
      setCreating(false);
    }
  };

  // ── Export do Money S3 ─────────────────────────────────────────────
  const [exportingMoney, setExportingMoney] = useState(false);

  // `ids` dovoluje exportovat i jednu fakturu z řádku tabulky.
  const handleExportMoney = async (ids = selectedIds) => {
    if (!ids.length || exportingMoney) return;
    setExportingMoney(true);
    try {
      await exportMoneyXml(ids);
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setExportingMoney(false);
    }
  };

  // ── Export do Excelu ───────────────────────────────────────────────
  const [exportingXlsx, setExportingXlsx] = useState(false);

  // Sloupce odpovídají výpisu; `ids` dovoluje export jedné faktury z řádku.
  const handleExportXlsx = async (ids = selectedIds) => {
    if (!ids.length || exportingXlsx) return;
    setExportingXlsx(true);
    try {
      await exportInvoicesXlsx(ids);
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setExportingXlsx(false);
    }
  };

  // ── Export do PDF ──────────────────────────────────────────────────
  const [exportingPdf, setExportingPdf] = useState(false);

  // Jedna faktura se otevře rovnou v prohlížeči, víc jich backend slepí do jednoho
  // PDF — účetní ho pošle na tiskárnu jedním příkazem.
  const handleExportPdf = async (ids = selectedIds) => {
    if (!ids.length || exportingPdf) return;
    if (ids.length === 1) {
      window.open(api.getInvoicePdfUrl(ids[0]), '_blank', 'noopener');
      return;
    }
    setExportingPdf(true);
    try {
      const { blob, filename, stats, warnings } = await api.exportInvoicesToPdf(ids);
      downloadBlob(blob, filename);
      showToast(`Staženo ${stats.total} ${sklonFaktur(stats.total)} v jednom PDF (${filename}).`);
      if (warnings.length) {
        console.warn('[Export PDF] varování:', warnings);
        showToast(`${warnings.length}× fakturu se nepodařilo vygenerovat, detail je v konzoli prohlížeče.`, 'warning');
      }
    } catch (e) {
      showToast(e.message, 'danger');
    } finally {
      setExportingPdf(false);
    }
  };

  const handleRestore = async (inv) => {
    try {
      await api.restoreInvoice(inv.id);
      showToast(`Faktura ${inv.number || inv.id} byla obnovená.`, 'success');
      fetchInvoices();
      window.dispatchEvent(new CustomEvent('orders:refresh'));
    } catch (e) {
      showToast(e.message || 'Fakturu se nepodařilo obnovit.', 'danger');
    }
  };

  // ── Součet vybraných ───────────────────────────────────────────────
  const [showSumModal, setShowSumModal] = useState(false);

  // Měny se sčítají zvlášť, kurz tu nepřepočítáváme.
  const sumByCurrency = () => {
    const map = new Map();
    selectedRows.forEach((inv) => {
      const currency = inv.currency || 'CZK';
      const row = map.get(currency) || { currency, count: 0, net: 0, total: 0 };
      row.count += 1;
      row.net += parseFloat(inv.net_net_price) || 0;
      row.total += parseFloat(inv.total_price) || 0;
      map.set(currency, row);
    });
    return [...map.values()];
  };

  // ── Odeslání e-mailem ──────────────────────────────────────────────
  const [emailInvoices, setEmailInvoices] = useState(null);

  // ── Pagination helpers ─────────────────────────────────────────────
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (safePage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      } else if (safePage >= totalPages - 2) {
        pages.push(1);
        pages.push('...');
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push('...');
        for (let i = safePage - 1; i <= safePage + 1; i++) pages.push(i);
        pages.push('...');
        pages.push(totalPages);
      }
    }
    return pages;
  };

  const startIdx = (safePage - 1) * ITEMS_PER_PAGE;

  // ── Render ─────────────────────────────────────────────────────────
  return (
    <Container fluid className="px-4" ref={containerRef}>
      {/* Header Bar */}
      <div className="header-bar mb-4">
        <Container fluid className="px-4">
          <div className="row align-items-center">
            <div className="col-md-6">
              <h4 className="mb-0">
                <i className="bi bi-receipt me-2"></i>Fakturace
              </h4>
            </div>
            <div className="col-md-6 text-end">
              <Button variant="outline-secondary" size="sm" className="me-2" onClick={() => fetchInvoices()} title="Načíst znovu">
                <i className="bi bi-arrow-clockwise"></i>
              </Button>
              <ColumnWidthsReset maUlozene={maUlozene} vychozi={vychozi} className="me-2" />
              <Button
                variant="primary"
                size="sm"
                className="me-2"
                onClick={otevriNovouFakturu}
                title="Faktura bez vazby na objednávku — položky se dopíšou ručně"
              >
                <i className="bi bi-plus-lg me-1"></i>Vytvořit fakturu
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                className="me-2"
                disabled={!hasSelection || exportingMoney}
                onClick={() => handleExportMoney()}
                title={hasSelection ? 'Vygeneruje XML feed pro import do Money S3' : 'Nejdřív vyber faktury v seznamu'}
              >
                <i className="bi bi-box-arrow-up me-1"></i>
                {exportingMoney ? 'Generuji…' : `Odeslat do MONEY${hasSelection ? ` (${selectedRows.size})` : ''}`}
              </Button>
              <Button
                variant="outline-secondary"
                size="sm"
                onClick={() => openTab('payment-matching', 'Párování plateb', '/invoices/payment-matching', 'PaymentMatching')}
              >
                <i className="bi bi-bank me-1"></i>Párování plateb
              </Button>
            </div>
          </div>
        </Container>
      </div>

      {/* Filters and Table */}
      <div className="table-container">
        {/* Filter bar */}
        <div className="row g-3 align-items-end mb-4">
          <div className="col-12 col-md-3">
            <label className="form-label">Hledat číslo, firma, VS...</label>
            <InputGroup>
              <span className="input-group-text bg-white border-end-0">
                <i className="bi bi-search"></i>
              </span>
              <Form.Control
                className="border-start-0 search-box"
                placeholder="číslo, firma, VS, obchodník, projekt..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
              />
            </InputGroup>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Typ</label>
            <Form.Select value={typeFilter} onChange={(e) => { setTypeFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Vše</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.label}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Typ platby</label>
            <Form.Select value={paymentTypeFilter} onChange={(e) => { setPaymentTypeFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Vše</option>
              {paymentTypes.map(pt => (
                <option key={pt.id} value={pt.id}>{pt.label}</option>
              ))}
            </Form.Select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Zasílání</label>
            <Form.Select value={communicationFilter} onChange={(e) => { setCommunicationFilter(e.target.value); setCurrentPage(1); }}>
              <option value="">Vše</option>
              {communicationMethods.map(cm => (
                <option key={cm.id} value={cm.id}>{cm.label}</option>
              ))}
              <option value="none">Nevyplněno</option>
            </Form.Select>
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Datum od</label>
            <Form.Control
              type="date"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="col-6 col-md-2">
            <label className="form-label">Datum do</label>
            <Form.Control
              type="date"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setCurrentPage(1); }}
            />
          </div>
          <div className="col-12 col-md-1">
            <Button variant="outline-secondary" className="w-100" onClick={handleReset} title="Reset filtrů">
              <i className="bi bi-arrow-counterclockwise"></i>
            </Button>
          </div>
        </div>

        {/* Quick filter pills — all invoices under "Vše" for now */}
        <div className="invoice-quick-filters">
          <button
            type="button"
            className={`quick-filter-btn${showDeleted ? '' : ' active'}`}
            onClick={() => { setShowDeleted(false); setCurrentPage(1); setSelectedRows(new Map()); }}
          >
            Vše
            {!showDeleted && <span className="filter-count">{totalItems}</span>}
          </button>
          <button
            type="button"
            className={`quick-filter-btn${showDeleted ? ' active' : ''}`}
            onClick={() => { setShowDeleted(true); setCurrentPage(1); setSelectedRows(new Map()); }}
            title="Smazané doklady — jde je odsud vrátit zpět"
          >
            <i className="bi bi-trash me-1" />Smazané
            {showDeleted && <span className="filter-count">{totalItems}</span>}
          </button>
        </div>

        {/* Export bar */}
        <div className="invoice-export-bar">
          <OverlayTrigger placement="top" overlay={<Tooltip>Jedna faktura se otevře v prohlížeči, víc jich stáhne jako jedno PDF za sebou</Tooltip>}>
            <span>
              <Button size="sm" variant="outline-secondary" disabled={!hasSelection || exportingPdf} onClick={() => handleExportPdf()}>
                {exportingPdf
                  ? <><Spinner animation="border" size="sm" className="me-1" />Generuji…</>
                  : <><i className="bi bi-file-earmark-pdf me-1" />Export PDF</>}
              </Button>
            </span>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>XML feed pro import do Money S3</Tooltip>}>
            <span>
              <Button size="sm" variant="outline-secondary" disabled={!hasSelection || exportingMoney} onClick={() => handleExportMoney()}>
                {exportingMoney
                  ? <><Spinner animation="border" size="sm" className="me-1" />Generuji…</>
                  : <><i className="bi bi-filetype-xml me-1" />Export XML</>}
              </Button>
            </span>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Vybrané faktury do Excelu — stejné sloupce jako tenhle výpis</Tooltip>}>
            <span>
              <Button size="sm" variant="outline-secondary" disabled={!hasSelection || exportingXlsx} onClick={() => handleExportXlsx()}>
                {exportingXlsx
                  ? <><Spinner animation="border" size="sm" className="me-1" />Generuji…</>
                  : <><i className="bi bi-file-earmark-excel me-1" />Export Excel</>}
              </Button>
            </span>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Zobrazit součet vybraných</Tooltip>}>
            <span>
              <Button size="sm" variant="outline-secondary" disabled={!hasSelection} onClick={() => setShowSumModal(true)}>
                <i className="bi bi-calculator me-1" />Součet
              </Button>
            </span>
          </OverlayTrigger>
          <OverlayTrigger placement="top" overlay={<Tooltip>Poslat vybrané e-mailem</Tooltip>}>
            <span>
              <Button size="sm" variant="outline-secondary" disabled={!hasSelection} onClick={() => setEmailInvoices([...selectedRows.values()])}>
                <i className="bi bi-envelope me-1" />Poslat e-mailem
              </Button>
            </span>
          </OverlayTrigger>
          {hasSelection && (
            <span className="small-muted">
              Vybráno: {selectedRows.size} {sklonFaktur(selectedRows.size)}
            </span>
          )}
          <span className="ms-auto small-muted">
            Nalezeno: {totalItems} záznamů
          </span>
        </div>

        {/* Table with loading overlay */}
        <div className="table-loading-wrapper" style={{ position: 'relative' }}>
          {loading && (
            <div className="table-loading-overlay">
              <div className="spinner-border spinner-border-sm text-primary" role="status">
                <span className="visually-hidden">Načítání...</span>
              </div>
            </div>
          )}
          <div className="table-responsive grid-wrap" ref={wrapRef} style={{ opacity: loading ? 0.4 : 1, transition: 'opacity 0.2s ease', pointerEvents: loading ? 'none' : 'auto' }}>
            <Table hover className="align-middle invoice-table grid-resizable" style={tableStyle}>
              {colgroup && (
                <colgroup>
                  {colgroup.map(c => <col key={c.key} style={{ width: c.width }} />)}
                </colgroup>
              )}
              <thead className="table-light">
                <tr>
                  <th>
                    <Form.Check
                      type="checkbox"
                      checked={allChecked}
                      onChange={toggleAll}
                    />
                    <span {...resizerProps('vyber')} />
                  </th>
                  <th role="button" onClick={() => handleSort('number')}>Číslo dokladu <SortIcon column="number" /><span {...resizerProps('cislo')} /></th>
                  <th role="button" onClick={() => handleSort('type')}>Typ <SortIcon column="type" /><span {...resizerProps('typ')} /></th>
                  <th role="button" onClick={() => handleSort('customer_name')}>Zákazník <SortIcon column="customer_name" /><span {...resizerProps('zakaznik')} /></th>
                  <th>Obchodník<span {...resizerProps('obchodnik')} /></th>
                  <th>Projekt<span {...resizerProps('projekt')} /></th>
                  <th role="button" onClick={() => handleSort('creation_date')}>Datum vystavení <SortIcon column="creation_date" /><span {...resizerProps('vystaveni')} /></th>
                  <th role="button" onClick={() => handleSort('tax_support_date')}>DÚZP <SortIcon column="tax_support_date" /><span {...resizerProps('duzp')} /></th>
                  <th role="button" onClick={() => handleSort('due_date')}>Splatnost <SortIcon column="due_date" /><span {...resizerProps('splatnost')} /></th>
                  <th>Typ platby<span {...resizerProps('typPlatby')} /></th>
                  <th>Zasílání<span {...resizerProps('zasilani')} /></th>
                  <th>Datum posl. úhrady<span {...resizerProps('posledniUhrada')} /></th>
                  <th role="button" className="text-end" onClick={() => handleSort('net_net_price')}>bez DPH <SortIcon column="net_net_price" /><span {...resizerProps('bezDph')} /></th>
                  <th role="button" className="text-end" onClick={() => handleSort('total_price')}>Celková cena <SortIcon column="total_price" /><span {...resizerProps('celkem')} /></th>
                  <th>Platby (VS)<span {...resizerProps('platby')} /></th>
                  <th>Status<span {...resizerProps('status')} /></th>
                  <th>Pohledávka<span {...resizerProps('pohledavka')} /></th>
                  <th className="text-end">Akce<span {...resizerProps('akce')} /></th>
                </tr>
              </thead>
              <tbody>
                {invoices.length === 0 && !loading ? (
                  <tr>
                    <td colSpan={18} className="text-center text-muted py-4">
                      Žádné záznamy odpovídající filtru
                    </td>
                  </tr>
                ) : (
                  invoices.map((inv) => {
                    const overdueDays = getOverdueDays(inv.due_date, inv.paid, inv.status);
                    const isCancelled = inv.status === 'storno';

                    // Collect variable symbols from payments (Income direction only)
                    const variableSymbols = (inv.payments || [])
                      .filter(p => p.variable_symbol && p.payment_direction === 'Income')
                      .map(p => String(p.variable_symbol));
                    const uniqueVS = [...new Set(variableSymbols)];

                    return (
                      <tr key={inv.id} className={isCancelled ? 'row-cancelled' : ''}>
                        <td>
                          <Form.Check
                            type="checkbox"
                            checked={selectedRows.has(inv.id)}
                            onChange={() => toggleRow(inv)}
                          />
                        </td>
                        <td className="text-nowrap">
                          <span className="link-document" onClick={() => openTab(`invoice-${inv.id}`, `Faktura ${inv.number || inv.id}`, `/invoices/${inv.id}`, 'InvoiceDetail', { invoiceId: inv.id })}>{inv.number || inv.id}</span>
                          {inv.sent_date && (
                            <OverlayTrigger placement="top" overlay={<Tooltip>Odesláno e-mailem {formatDate(inv.sent_date)}</Tooltip>}>
                              <i className="bi bi-envelope-check ms-2 text-success" />
                            </OverlayTrigger>
                          )}
                        </td>
                        <td>
                          <TypeBadge type={inv.type} label={inv.type_label} />
                        </td>
                        <td>
                          <span className="klient-cell">{inv.customer_name}</span>
                        </td>
                        <td>
                          {/* Obchodník se bere z objednávky, ne z faktury (viz
                              InvoiceRepository::doplnObchodniky). Velké rámcové objednávky
                              mají obchodníka na každé položce vlastního, takže faktura jich
                              může mít víc — stejný zápis jako u vydání. Kde obchodník
                              u položek chybí, je to jméno toho, kdo objednávku zadal,
                              a kurzíva s nápovědou říkají, že to není obchodník. */}
                          {(inv.salespeople || []).length > 0 ? (
                            <span
                              className={`small-muted text-nowrap${inv.salesperson_source !== 'item' ? ' fst-italic' : ''}`}
                              title={
                                (inv.salesperson_source !== 'item'
                                  ? (inv.salesperson_source === 'order'
                                    ? 'Obchodník u položek objednávky není uvedený — tohle je ten, kdo objednávku zadal.\n'
                                    : 'Doklad není navázaný na objednávku — jméno je z faktury.\n')
                                  : '') + inv.salespeople.join('\n')
                              }
                            >
                              {inv.salespeople[0]}
                              {inv.salespeople.length > 1 && ` + ${inv.salespeople.length - 1} další`}
                            </span>
                          ) : <span className="small-muted">—</span>}
                        </td>
                        <td>
                          {/* Faktura za víc vydání: ukáže se první a kolik dalších,
                              celý výpis je v nápovědě — jinak by řádek narostl. */}
                          {(inv.projects || []).length > 0 ? (
                            <span className="small-muted projekt-cell" title={inv.projects.join('\n')}>
                              {inv.projects[0]}
                              {inv.projects.length > 1 && ` + ${inv.projects.length - 1} další`}
                            </span>
                          ) : <span className="small-muted">—</span>}
                        </td>
                        <td><span className="small-muted">{formatDate(inv.creation_date)}</span></td>
                        <td><span className="small-muted">{formatDate(inv.tax_support_date)}</span></td>
                        <td><span className="small-muted">{formatDate(inv.due_date)}</span></td>
                        <td>
                          {inv.payment_type_label
                            ? <span className="badge-payment-type">{inv.payment_type_label}</span>
                            : <span className="text-muted">—</span>}
                        </td>
                        <td>
                          {/* Způsob zasílání z karty zákazníka — účetní podle něj pozná,
                              co poslat e-mailem a co vytisknout a dát na poštu. */}
                          <ZpusobZasilani method={inv.communication_method} label={inv.communication_method_label} />
                        </td>
                        <td>
                          {inv.last_payment_date
                            ? <span className="small-muted">{formatDate(inv.last_payment_date)}</span>
                            : <span className="no-data">—</span>}
                        </td>
                        <td className="price-muted">{formatPrice(inv.net_net_price)}</td>
                        <td className={`price-cell ${isCancelled ? 'text-decoration-line-through text-muted' : ''}`}>
                          {formatPrice(inv.total_price)} {inv.currency || 'CZK'}
                        </td>
                        <td>
                          {uniqueVS.length > 0
                            ? <span className="vs-list">{uniqueVS.join(', ')}</span>
                            : <span className="no-data">—</span>}
                        </td>
                        <td><StatusBadge status={inv.status} /></td>
                        <td>
                          {inv.status === 'uhrazena' || inv.status === 'storno'
                            ? <span className="badge-overdue badge-overdue-ok">OK</span>
                            : <OverdueBadge days={overdueDays} />}
                        </td>
                        <td>
                          <div className="invoice-action-buttons">
                            <OverlayTrigger placement="top" overlay={<Tooltip>Export PDF</Tooltip>}>
                              <Button size="sm" variant="outline-secondary" className="btn-action" onClick={() => handleExportPdf([inv.id])}>
                                <i className="bi bi-file-earmark-pdf" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Export do Excelu</Tooltip>}>
                              <Button size="sm" variant="outline-secondary" className="btn-action" disabled={exportingXlsx} onClick={() => handleExportXlsx([inv.id])}>
                                <i className="bi bi-file-earmark-excel" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Export XML pro Money S3</Tooltip>}>
                              <Button size="sm" variant="outline-secondary" className="btn-action" disabled={exportingMoney} onClick={() => handleExportMoney([inv.id])}>
                                <i className="bi bi-filetype-xml" />
                              </Button>
                            </OverlayTrigger>
                            <OverlayTrigger
                              placement="top"
                              overlay={<Tooltip>{inv.communication_method === 'POST' ? 'Doklad se posílá poštou' : 'Poslat e-mailem'}</Tooltip>}
                            >
                              <span>
                                <Button
                                  size="sm"
                                  variant="outline-secondary"
                                  className="btn-action"
                                  disabled={inv.communication_method === 'POST'}
                                  onClick={() => setEmailInvoices([inv])}
                                >
                                  <i className="bi bi-envelope" />
                                </Button>
                              </span>
                            </OverlayTrigger>
                            <OverlayTrigger placement="top" overlay={<Tooltip>Upravit</Tooltip>}>
                              <Button size="sm" variant="outline-primary" className="btn-action" onClick={() => openTab(`invoice-${inv.id}`, `Faktura ${inv.number || inv.id}`, `/invoices/${inv.id}`, 'InvoiceDetail', { invoiceId: inv.id })}>
                                <i className="bi bi-pencil" />
                              </Button>
                            </OverlayTrigger>
                            {showDeleted ? (
                              <OverlayTrigger placement="top" overlay={<Tooltip>{canDelete ? 'Obnovit fakturu' : 'Nemáš oprávnění obnovovat faktury'}</Tooltip>}>
                                <span>
                                  <Button size="sm" variant="outline-success" className="btn-action" disabled={!canDelete} onClick={() => handleRestore(inv)}>
                                    <i className="bi bi-arrow-counterclockwise" />
                                  </Button>
                                </span>
                              </OverlayTrigger>
                            ) : (
                              <OverlayTrigger placement="top" overlay={<Tooltip>{canDelete ? 'Smazat fakturu' : 'Nemáš oprávnění mazat faktury'}</Tooltip>}>
                                <span>
                                  <Button size="sm" variant="outline-danger" className="btn-action" disabled={!canDelete} onClick={() => setDeleteInvoiceId(inv.id)}>
                                    <i className="bi bi-trash" />
                                  </Button>
                                </span>
                              </OverlayTrigger>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </Table>
          </div>
        </div>

        {/* Pagination */}
        <div className="d-flex justify-content-between align-items-center mt-4">
          <div>
            <span className="text-muted">
              Zobrazeno: {invoices.length > 0 ? startIdx + 1 : 0}–{startIdx + invoices.length} z {totalItems}
            </span>
          </div>
          <nav>
            <ul className="pagination mb-0">
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(1)} disabled={safePage === 1}>«</button>
              </li>
              <li className={`page-item ${safePage === 1 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))} disabled={safePage === 1}>‹</button>
              </li>
              {getPageNumbers().map((page, idx) => (
                <li key={idx} className={`page-item ${page === safePage ? 'active' : ''} ${page === '...' ? 'disabled' : ''}`}>
                  {page === '...' ? (
                    <span className="page-link">...</span>
                  ) : (
                    <button className="page-link" onClick={() => setCurrentPage(page)}>{page}</button>
                  )}
                </li>
              ))}
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))} disabled={safePage >= totalPages}>›</button>
              </li>
              <li className={`page-item ${safePage >= totalPages || totalPages === 0 ? 'disabled' : ''}`}>
                <button className="page-link" onClick={() => setCurrentPage(totalPages)} disabled={safePage >= totalPages}>»</button>
              </li>
            </ul>
          </nav>
        </div>
      </div>

      {/* Součet vybraných faktur */}
      <Modal show={showSumModal} onHide={() => setShowSumModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title><i className="bi bi-calculator me-2" />Součet vybraných</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3">
            Vybráno <strong>{selectedRows.size}</strong> {sklonFaktur(selectedRows.size)}.
          </div>
          <Table size="sm" className="align-middle mb-0">
            <thead className="table-light">
              <tr>
                <th>Měna</th>
                <th className="text-end">Počet</th>
                <th className="text-end">Bez DPH</th>
                <th className="text-end">S DPH</th>
              </tr>
            </thead>
            <tbody>
              {sumByCurrency().map((row) => (
                <tr key={row.currency}>
                  <td>{row.currency}</td>
                  <td className="text-end">{row.count}</td>
                  <td className="text-end">{formatPrice(row.net)}</td>
                  <td className="text-end fw-bold">{formatPrice(row.total)}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowSumModal(false)}>Zavřít</Button>
        </Modal.Footer>
      </Modal>

      <InvoiceDeleteModal
        show={deleteInvoiceId !== null}
        invoiceId={deleteInvoiceId}
        onHide={() => setDeleteInvoiceId(null)}
      />

      <InvoiceEmailModal
        show={emailInvoices !== null}
        invoices={emailInvoices}
        onHide={() => setEmailInvoices(null)}
      />

      {/* Nová faktura bez objednávky — jen hlavička, položky se dopisují v detailu. */}
      <Modal show={showCreateModal} onHide={() => setShowCreateModal(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title>Nová faktura</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Group className="mb-3">
            <Form.Label>Zákazník <span className="text-danger">*</span></Form.Label>
            <SearchableSelect
              value={createForm.customerId || ''}
              selectedLabel={createForm.customerLabel}
              onSearch={searchCompanies}
              onChange={(id, opt) => setCreateForm(f => ({
                ...f,
                customerId: id ? parseInt(id) : '',
                customerLabel: opt ? opt.label : '',
              }))}
              placeholder="Hledat firmu…"
              emptyLabel="— Vyberte zákazníka —"
            />
          </Form.Group>

          <div className="row g-3 mb-3">
            <div className="col-6">
              <Form.Label>Typ dokladu</Form.Label>
              <Form.Select
                value={createForm.type}
                onChange={(e) => setCreateForm(f => ({ ...f, type: e.target.value }))}
              >
                {types.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
              </Form.Select>
            </div>
            <div className="col-6">
              <Form.Label>Typ platby</Form.Label>
              <Form.Select
                value={createForm.paymentType}
                onChange={(e) => setCreateForm(f => ({ ...f, paymentType: e.target.value }))}
              >
                {paymentTypes.map(pt => <option key={pt.id} value={pt.id}>{pt.label}</option>)}
              </Form.Select>
            </div>
            <div className="col-6">
              <Form.Label>DUZP</Form.Label>
              <Form.Control
                type="date"
                value={createForm.taxSupportDate}
                onChange={(e) => setCreateForm(f => ({ ...f, taxSupportDate: e.target.value }))}
              />
            </div>
            <div className="col-6">
              <Form.Label>Splatnost</Form.Label>
              <Form.Control
                type="date"
                value={createForm.dueDate}
                onChange={(e) => setCreateForm(f => ({ ...f, dueDate: e.target.value }))}
              />
              <Form.Text className="text-muted">Prázdné = podle karty zákazníka.</Form.Text>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={() => setShowCreateModal(false)} disabled={creating}>
            Zrušit
          </Button>
          <Button variant="primary" onClick={vytvorFakturu} disabled={creating || !createForm.customerId}>
            {creating ? <><Spinner size="sm" animation="border" className="me-2" />Vytvářím…</> : 'Vytvořit fakturu'}
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
}

export default InvoiceList;
